import { FC, useState } from 'react'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { ReactComponent as EyeClosedIcon } from 'src/assets/svg/eyeclosed.svg'
import { ReactComponent as EyeIcon } from 'src/assets/svg/eye.svg'
import { ReactComponent as TickIcon } from 'src/assets/svg/tick.svg'
import styles from './changePasswordModal.module.scss'
import CardStatusDialog from '../../Subscription/PaymentMethod/CardStatusDialog'

interface ChangePasswordModalProps {
  onClose: () => void
  onSave: (currentPassword: string, newPassword: string) => void
}

interface PasswordValidation {
  hasLowercase: boolean
  hasSpecialChar: boolean
  hasUppercase: boolean
  hasValidLength: boolean
}

const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  onClose,
  onSave,
}) => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showValidationTooltip, setShowValidationTooltip] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const validatePassword = (password: string): PasswordValidation => {
    return {
      hasLowercase: /[a-z]/.test(password),
      hasSpecialChar: /[!@#$%^&*]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasValidLength: password.length >= 8 && password.length <= 20,
    }
  }

  const validation = validatePassword(newPassword)
  const isPasswordValid =
    validation.hasLowercase &&
    validation.hasUppercase &&
    validation.hasSpecialChar &&
    validation.hasValidLength

  const showPasswordMismatch =
    confirmPassword !== '' && newPassword !== confirmPassword

  const canSave =
    currentPassword.trim() !== '' &&
    newPassword.trim() !== '' &&
    confirmPassword.trim() !== '' &&
    newPassword === confirmPassword &&
    isPasswordValid

  const handleSave = async () => {
    if (!canSave) return
    await onSave(currentPassword, newPassword)
    setShowSuccessDialog(true)
  }

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false)
    onClose()
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Change Password</h2>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </div>

        <p className={styles.description}>
          For security reasons, we need your current password before updating to
          a new one. This helps verify your identity and keep your account safe.
        </p>

        <div className={styles.formGroup}>
          <label className={styles.label}>Current Password</label>
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
            />
            <button
              className={styles.eyeBtn}
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              type="button"
            >
              {showCurrentPassword ? <EyeIcon /> : <EyeClosedIcon />}
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>New Password</label>
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              onBlur={() => setShowValidationTooltip(false)}
              onChange={(e) => setNewPassword(e.target.value)}
              onFocus={() => setShowValidationTooltip(true)}
              placeholder="Enter your new password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
            />
            <button
              className={styles.eyeBtn}
              onClick={() => setShowNewPassword(!showNewPassword)}
              type="button"
            >
              {showNewPassword ? <EyeIcon /> : <EyeClosedIcon />}
            </button>
            {showValidationTooltip && (
              <div className={styles.validationTooltip}>
                <div
                  className={`${styles.validationItem} ${validation.hasLowercase ? styles.valid : ''}`}
                >
                  <span className={styles.checkIcon}>
                    <TickIcon />
                  </span>
                  Minimum one lowercase letter
                </div>
                <div
                  className={`${styles.validationItem} ${validation.hasUppercase ? styles.valid : ''}`}
                >
                  <span className={styles.checkIcon}>
                    <TickIcon />
                  </span>
                  Minimum one capital letter
                </div>
                <div
                  className={`${styles.validationItem} ${validation.hasSpecialChar ? styles.valid : ''}`}
                >
                  <span className={styles.checkIcon}>
                    <TickIcon />
                  </span>
                  Minimum one character (!@#$%^&*)
                </div>
                <div
                  className={`${styles.validationItem} ${validation.hasValidLength ? styles.valid : ''}`}
                >
                  <span className={styles.checkIcon}>
                    <TickIcon />
                  </span>
                  Minimum 8 and maximum 20 Characters
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Confirm New Password</label>
          <div className={styles.inputWrapper}>
            <input
              className={`${styles.input} ${showPasswordMismatch ? styles.error : ''}`}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
            />
            <button
              className={styles.eyeBtn}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              type="button"
            >
              {showConfirmPassword ? <EyeIcon /> : <EyeClosedIcon />}
            </button>
          </div>
          {showPasswordMismatch && (
            <p className={styles.errorMessage}>* Passwords do not match.</p>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <button className={styles.cancelBtn} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            disabled={!canSave}
            onClick={handleSave}
            type="button"
          >
            Save
          </button>
        </div>
      </div>

      <CardStatusDialog
        isOpen={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        type="passwordUpdated"
      />
    </div>
  )
}

export default ChangePasswordModal
